import { Fragment, useCallback } from "react";
import { formatDateAsISO, formatRawAsLocal } from "shared-utils/src/datetime";
import { useCountdown, useMediaQuery } from "../../hooks";
import styles from "./CountdownTimer.module.css";
import type {
  CountdownDisplayProps,
  CountdownTimerProps,
  UnitKey,
} from "./types";
import { defaultLabels, formatTime, unitKeys } from "./utils";

const CountdownDisplay = ({
  values,
  timeLeft,
  targetDate,
  ariaLabel,
  labels,
}: CountdownDisplayProps) => (
  <div role="timer" aria-label={ariaLabel}>
    <time
      dateTime={formatDateAsISO(targetDate)}
      className={styles.timerContainer}
    >
      {unitKeys.map((key, index) => {
        const unitLabel = labels[key](timeLeft[key]);
        return (
          <Fragment key={key}>
            <span className={styles.timeUnit}>
              <div className={styles.labelStyle}>{unitLabel}</div>
              <div>{values[key]}</div>
            </span>
            {index < unitKeys.length - 1 && (
              <span className={styles.separator} aria-hidden="true">
                :
              </span>
            )}
          </Fragment>
        );
      })}
    </time>
  </div>
);

const CountdownTimer = ({
  targetDate,
  onComplete,
  labels = defaultLabels,
}: CountdownTimerProps) => {
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  const handleComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  const timeLeft = useCountdown(targetDate, {
    onComplete: handleComplete,
  });

  if (!targetDate || !timeLeft) {
    return null;
  }

  if (prefersReducedMotion) {
    const formatted = formatRawAsLocal(
      targetDate.toISOString(),
      "E d MMM, HH:mm",
    );
    const label = labels.deadlineAt(formatted);

    return (
      <div role="timer" aria-label={label}>
        <time dateTime={formatDateAsISO(targetDate)}>{formatted}</time>
      </div>
    );
  }

  const isExpired = timeLeft.total <= 0;

  const values: Record<UnitKey, string> = isExpired
    ? {
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      }
    : {
        days: formatTime(timeLeft.days),
        hours: formatTime(timeLeft.hours),
        minutes: formatTime(timeLeft.minutes),
        seconds: formatTime(timeLeft.seconds),
      };

  const ariaLabel = isExpired
    ? labels.expired
    : labels.timeRemaining({
        days: timeLeft.days,
        daysLabel: labels.days(timeLeft.days),
        hours: timeLeft.hours,
        hoursLabel: labels.hours(timeLeft.hours),
        minutes: timeLeft.minutes,
        minutesLabel: labels.minutes(timeLeft.minutes),
        seconds: timeLeft.seconds,
        secondsLabel: labels.seconds(timeLeft.seconds),
      });

  return (
    <CountdownDisplay
      values={values}
      timeLeft={timeLeft}
      targetDate={targetDate}
      ariaLabel={ariaLabel}
      labels={labels}
    />
  );
};

export default CountdownTimer;
